import type { MedicalEvent, BodyMetricFamily, BodyMeasurementPayload, MeasurementDisplayUnit } from "@medassist/shared";
import {
  localCalendarDate, BODY_METRIC_BY_ID, symptomFollowUpKey,
  MEASUREMENT_SITE_BY_ID, DEFAULT_MEASUREMENT_UNIT, toDisplay,
} from "@medassist/shared";

// Series builders + chart-type config (CHARTS_REDESIGN_PLAN.md §4).
// Moved out of ChartsPane.tsx; logic unchanged except added `key`/`unit`/`group`
// fields on ChartSeries. Charts are client-computed from listEvents (PLAN.md §24.2).

export interface DataPoint {
  x: number; // timestamp ms
  y: number;
  label?: string;
}

export interface ChartSeries {
  key: string; // stable id, e.g. "bp_systolic"
  name: string; // display name
  points: DataPoint[]; // sorted ascending by x
  yLabel?: string;
  color?: string;
  refLow?: number;
  refHigh?: number;
  unit?: string; // used by CSV export + tooltip
  group?: string; // series sharing a group may be overlaid on one chart
}

export type ChartType = "symptom" | "vital" | "sleep" | "mood" | "hydration" | "lab" | "body" | "measurement";

export function buildSymptomSeries(events: MedicalEvent[]): Map<string, ChartSeries> {
  const pts = new Map<string, DataPoint[]>();
  const displayNames = new Map<string, string>(); // canonical key → first-seen raw name
  for (const e of events.filter((ev) => ev.eventType === "symptom" || ev.eventType === "headache")) {
    const p = e.payload as Record<string, unknown>;
    const rawName = e.eventType === "headache" ? "headache" : String(p.name ?? "symptom");
    const key = symptomFollowUpKey(rawName);
    const sev = Number(p.severity);
    if (!isNaN(sev)) {
      if (!displayNames.has(key)) displayNames.set(key, rawName);
      const existing = pts.get(key) ?? [];
      existing.push({ x: new Date(e.occurredAt).getTime(), y: sev, label: `${new Date(e.occurredAt).toLocaleDateString()}: ${sev}/10` });
      pts.set(key, existing);
    }
  }
  const result = new Map<string, ChartSeries>();
  for (const [key, points] of pts) {
    const name = displayNames.get(key) ?? key;
    result.set(key, { key, name, points: points.sort((a, b) => a.x - b.x), yLabel: "severity (1–10)", color: "#ef4444", unit: "/10" });
  }
  return result;
}

export function buildVitalSeries(events: MedicalEvent[]): Map<string, ChartSeries> {
  const map = new Map<string, DataPoint[]>();
  for (const e of events.filter((ev) => ev.eventType === "vital_sign")) {
    const p = e.payload as Record<string, unknown>;
    const kind = String(p.kind ?? "");
    const x = new Date(e.occurredAt).getTime();
    if (kind === "bp") {
      const sys = Number(p.systolic);
      const dia = Number(p.diastolic);
      if (!isNaN(sys)) {
        const pts = map.get("bp_systolic") ?? [];
        pts.push({ x, y: sys, label: `${new Date(e.occurredAt).toLocaleDateString()}: ${sys} mmHg` });
        map.set("bp_systolic", pts);
      }
      if (!isNaN(dia)) {
        const pts = map.get("bp_diastolic") ?? [];
        pts.push({ x, y: dia, label: `${new Date(e.occurredAt).toLocaleDateString()}: ${dia} mmHg` });
        map.set("bp_diastolic", pts);
      }
    } else {
      const val = Number(p.value);
      if (!isNaN(val)) {
        const pts = map.get(kind) ?? [];
        pts.push({ x, y: val, label: `${new Date(e.occurredAt).toLocaleDateString()}: ${val} ${p.unit ?? ""}` });
        map.set(kind, pts);
      }
    }
  }
  const labels: Record<string, string> = {
    bp_systolic: "BP systolic (mmHg)", bp_diastolic: "BP diastolic (mmHg)",
    hr: "Heart rate (bpm)", temp: "Temperature", weight: "Weight",
    spo2: "SpO₂ (%)", glucose: "Blood glucose", respiratory_rate: "Resp. rate",
  };
  const units: Record<string, string> = {
    bp_systolic: "mmHg", bp_diastolic: "mmHg", hr: "bpm", spo2: "%",
  };
  const colors: Record<string, string> = {
    bp_systolic: "#f97316", bp_diastolic: "#fb923c", hr: "#ef4444",
    temp: "#a78bfa", weight: "#10b981", spo2: "#3b82f6", glucose: "#f59e0b",
    respiratory_rate: "#6366f1",
  };
  const result = new Map<string, ChartSeries>();
  for (const [name, pts] of map) {
    result.set(name, {
      key: name,
      name: labels[name] ?? name,
      points: pts.sort((a, b) => a.x - b.x),
      yLabel: labels[name] ?? name,
      color: colors[name] ?? "#6b7280",
      unit: units[name],
      group: name === "bp_systolic" || name === "bp_diastolic" ? "bp" : undefined,
    });
  }
  return result;
}

export function buildSleepSeries(events: MedicalEvent[]): Map<string, ChartSeries> {
  const hoursPoints: DataPoint[] = [];
  const qualityPoints: DataPoint[] = [];
  for (const e of events.filter((ev) => ev.eventType === "sleep")) {
    const p = e.payload as Record<string, unknown>;
    const x = new Date(e.occurredAt).getTime();
    const hrs = Number(p.totalSleepHours);
    const qual = Number(p.qualityRating);
    const label = new Date(e.occurredAt).toLocaleDateString();
    if (!isNaN(hrs)) hoursPoints.push({ x, y: hrs, label: `${label}: ${hrs}h` });
    if (!isNaN(qual)) qualityPoints.push({ x, y: qual, label: `${label}: ${qual}/10` });
  }
  const result = new Map<string, ChartSeries>();
  if (hoursPoints.length) result.set("sleep_hours", { key: "sleep_hours", name: "Sleep hours", points: hoursPoints.sort((a, b) => a.x - b.x), yLabel: "hours", color: "#6366f1", unit: "h" });
  if (qualityPoints.length) result.set("sleep_quality", { key: "sleep_quality", name: "Sleep quality (1–10)", points: qualityPoints.sort((a, b) => a.x - b.x), yLabel: "1–10", color: "#8b5cf6", unit: "/10" });
  return result;
}

export function buildMoodSeries(events: MedicalEvent[]): Map<string, ChartSeries> {
  const moodPts: DataPoint[] = [];
  const anxietyPts: DataPoint[] = [];
  const energyPts: DataPoint[] = [];
  for (const e of events.filter((ev) => ev.eventType === "mood")) {
    const p = e.payload as Record<string, unknown>;
    const x = new Date(e.occurredAt).getTime();
    const label = new Date(e.occurredAt).toLocaleDateString();
    const mood = Number(p.mood);
    if (!isNaN(mood)) moodPts.push({ x, y: mood, label: `${label}: ${mood}/10` });
    const anx = Number(p.anxiety);
    if (!isNaN(anx)) anxietyPts.push({ x, y: anx, label: `${label}: anx ${anx}` });
    const eng = Number(p.energy);
    if (!isNaN(eng)) energyPts.push({ x, y: eng, label: `${label}: energy ${eng}` });
  }
  const result = new Map<string, ChartSeries>();
  if (moodPts.length) result.set("mood", { key: "mood", name: "Mood (1–10)", points: moodPts.sort((a, b) => a.x - b.x), yLabel: "1–10", color: "#f59e0b", unit: "/10", group: "mood" });
  if (anxietyPts.length) result.set("anxiety", { key: "anxiety", name: "Anxiety (1–10)", points: anxietyPts.sort((a, b) => a.x - b.x), yLabel: "1–10", color: "#ef4444", unit: "/10", group: "mood" });
  if (energyPts.length) result.set("energy", { key: "energy", name: "Energy (1–10)", points: energyPts.sort((a, b) => a.x - b.x), yLabel: "1–10", color: "#10b981", unit: "/10", group: "mood" });
  return result;
}

export function buildHydrationSeries(events: MedicalEvent[], zone: string): Map<string, ChartSeries> {
  // Aggregate daily mL totals
  const byDay = new Map<string, number>();
  for (const e of events.filter((ev) => ev.eventType === "hydration")) {
    const p = e.payload as Record<string, unknown>;
    const ml = Number(p.volumeMl);
    if (isNaN(ml)) continue;
    const day = localCalendarDate(e.occurredAt, zone); // D3 fix: bucket by local day
    byDay.set(day, (byDay.get(day) ?? 0) + ml);
  }
  const points = Array.from(byDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, ml]) => ({ x: new Date(day).getTime(), y: ml, label: `${day}: ${ml} mL` }));
  const result = new Map<string, ChartSeries>();
  if (points.length) result.set("hydration", { key: "hydration", name: "Daily hydration (mL)", points, yLabel: "mL/day", color: "#0ea5e9", unit: "mL" });
  return result;
}

export function buildLabSeries(events: MedicalEvent[]): Map<string, ChartSeries> {
  const map = new Map<string, { pts: DataPoint[]; refLow?: number; refHigh?: number; unit?: string }>();
  for (const e of events.filter((ev) => ev.eventType === "lab_result")) {
    const p = e.payload as Record<string, unknown>;
    const analyte = String(p.analyte ?? "");
    const val = Number(p.value);
    if (!analyte || isNaN(val)) continue;
    const x = new Date(e.occurredAt).getTime();
    const label = `${new Date(e.occurredAt).toLocaleDateString()}: ${val} ${p.unit ?? ""}`;
    const entry = map.get(analyte) ?? { pts: [], refLow: Number(p.refLow) || undefined, refHigh: Number(p.refHigh) || undefined, unit: String(p.unit ?? "") };
    entry.pts.push({ x, y: val, label });
    map.set(analyte, entry);
  }
  const result = new Map<string, ChartSeries>();
  for (const [analyte, { pts, refLow, refHigh, unit }] of map) {
    result.set(analyte, {
      key: analyte,
      name: `${analyte}${unit ? ` (${unit})` : ""}`,
      points: pts.sort((a, b) => a.x - b.x),
      yLabel: unit ?? analyte,
      color: "#059669",
      unit,
      refLow,
      refHigh,
    });
  }
  return result;
}

// Per-family colors for body-composition metric series (BODY_COMPOSITION_PLAN.md §7).
const BODY_FAMILY_COLORS: Record<BodyMetricFamily, string> = {
  composition: "#10b981",
  dimensions: "#6366f1",
  posture: "#f59e0b",
  visual: "#ec4899",
};

/** One series per body metric id seen across the patient's body_composition events.
 *  Series are grouped by family so related metrics can be overlaid (D-BC-6: manual
 *  vs. vision provenance is carried in each point's label). */
export function buildBodyCompositionSeries(events: MedicalEvent[]): Map<string, ChartSeries> {
  const map = new Map<string, DataPoint[]>();
  for (const e of events.filter((ev) => ev.eventType === "body_composition")) {
    const metrics = (e.payload as Record<string, unknown>).metrics as
      | Record<string, { value?: number; unit?: string; source?: string }>
      | undefined;
    if (!metrics) continue;
    const x = new Date(e.occurredAt).getTime();
    const day = new Date(e.occurredAt).toLocaleDateString();
    for (const [id, m] of Object.entries(metrics)) {
      const val = Number(m?.value);
      if (isNaN(val)) continue;
      const def = BODY_METRIC_BY_ID[id];
      const unit = m?.unit ?? def?.unit ?? "";
      const tag = m?.source === "manual" ? " (measured)" : "";
      const pts = map.get(id) ?? [];
      pts.push({ x, y: val, label: `${day}: ${val}${unit}${tag}` });
      map.set(id, pts);
    }
  }
  const result = new Map<string, ChartSeries>();
  for (const [id, pts] of map) {
    const def = BODY_METRIC_BY_ID[id];
    result.set(id, {
      key: id,
      name: def ? `${def.label}${def.unit ? ` (${def.unit})` : ""}` : id,
      points: pts.sort((a, b) => a.x - b.x),
      yLabel: def?.unit || def?.label || id,
      color: def ? BODY_FAMILY_COLORS[def.family] : "#6b7280",
      unit: def?.unit,
      group: def?.family,
    });
  }
  return result;
}

/** One series per measurement site seen across the patient's body_measurement
 *  events, converted to the given display unit (D-MEAS-4 — canonical storage
 *  stays cm; only the chart display changes). Custom sites fall back to their
 *  raw id as the label, same as an unrecognized body-composition metric id. */
export function buildMeasurementSeries(
  events: MedicalEvent[],
  unit: MeasurementDisplayUnit = DEFAULT_MEASUREMENT_UNIT,
): Map<string, ChartSeries> {
  const map = new Map<string, DataPoint[]>();
  for (const e of events.filter((ev) => ev.eventType === "body_measurement")) {
    const payload = e.payload as unknown as BodyMeasurementPayload;
    const x = new Date(e.occurredAt).getTime();
    const day = new Date(e.occurredAt).toLocaleDateString();
    for (const [siteId, v] of Object.entries(payload.sites ?? {})) {
      const y = toDisplay(v.cm, unit);
      const pts = map.get(siteId) ?? [];
      pts.push({ x, y, label: `${day}: ${y} ${unit}` });
      map.set(siteId, pts);
    }
  }
  const result = new Map<string, ChartSeries>();
  for (const [siteId, pts] of map) {
    result.set(siteId, {
      key: siteId,
      name: MEASUREMENT_SITE_BY_ID[siteId]?.label ?? siteId,
      points: pts.sort((a, b) => a.x - b.x),
      yLabel: unit,
      color: "#0ea5e9",
      unit,
    });
  }
  return result;
}

// Maps each chart type to the event types it needs.
export const CHART_TYPE_TO_EVENT_TYPES: Record<ChartType, string[]> = {
  symptom:   ["symptom", "headache"],
  vital:     ["vital_sign"],
  sleep:     ["sleep"],
  mood:      ["mood"],
  hydration: ["hydration"],
  lab:       ["lab_result"],
  body:      ["body_composition"],
  measurement: ["body_measurement"],
};

export interface ChartTypeMeta {
  key: ChartType;
  label: string;
}

export const CHART_TYPES: ChartTypeMeta[] = [
  { key: "symptom", label: "Symptoms" },
  { key: "vital", label: "Vitals" },
  { key: "sleep", label: "Sleep" },
  { key: "mood", label: "Mood" },
  { key: "hydration", label: "Hydration" },
  { key: "lab", label: "Labs" },
  { key: "body", label: "Body" },
  { key: "measurement", label: "Body measurements" },
];

export function buildSeriesFor(
  type: ChartType,
  events: MedicalEvent[],
  measurementUnit: MeasurementDisplayUnit | undefined,
  zone: string,
): Map<string, ChartSeries> {
  switch (type) {
    case "symptom": return buildSymptomSeries(events);
    case "vital": return buildVitalSeries(events);
    case "sleep": return buildSleepSeries(events);
    case "mood": return buildMoodSeries(events);
    case "hydration": return buildHydrationSeries(events, zone);
    case "lab": return buildLabSeries(events);
    case "body": return buildBodyCompositionSeries(events);
    case "measurement": return buildMeasurementSeries(events, measurementUnit);
  }
}
